import type { ApolloError } from '@apollo/client';
import { useQuery } from '@apollo/client';

import {
  ClientPlatform,
  FeatureListProduct,
  ProductType,
} from '@xing-com/crate-common-graphql-types';

import type { PerkGroup } from '../components/perk-list/perk-list.types';
import { FeatureListsDocument } from '../graphql/feature-lists.gql-types';
import type { FeatureListsQuery } from '../graphql/feature-lists.gql-types';

type FeatureList = NonNullable<
  NonNullable<FeatureListsQuery['viewer']>['premium']
>;

type PerkGroups = Record<ProductType, readonly PerkGroup[] | null>;

export type UseFeatureLists = {
  error: ApolloError | undefined;
  groups: PerkGroups;
  hasCompleteData: boolean;
  loading: boolean;
};

const unavailableGroups: PerkGroups = {
  [ProductType.Premium]: null,
  [ProductType.Projobs]: null,
};

export const mapFeatureList = (
  featureList: FeatureList | null | undefined
): readonly PerkGroup[] | null => {
  if (!featureList?.sections.some((section) => section.items.length > 0)) {
    return null;
  }

  return featureList.sections.map((section) => ({
    heading: section.title,
    perks: section.items.map((item) => ({
      isNew: item.isNew,
      text: item.text,
    })),
  }));
};

export const useFeatureLists = (): UseFeatureLists => {
  const { data, error, loading } = useQuery(FeatureListsDocument, {
    errorPolicy: 'all',
    variables: {
      platform: ClientPlatform.Web,
      premiumProduct: FeatureListProduct.Premium,
      projobsProduct: FeatureListProduct.Projobs,
    },
  });

  const groups = error
    ? unavailableGroups
    : {
        [ProductType.Premium]: mapFeatureList(data?.viewer?.premium),
        [ProductType.Projobs]: mapFeatureList(data?.viewer?.projobs),
      };
  const hasCompleteData =
    !loading &&
    !error &&
    groups[ProductType.Premium] !== null &&
    groups[ProductType.Projobs] !== null;

  return { error, groups, hasCompleteData, loading };
};
